// ============================================================
// EQUIPMENT TRACKER — Fase 10.4
// Rastreamento, saúde e análise de uso de equipamentos
// ============================================================
import { supabase } from '@/lib/supabase'
import type { Equipment, EquipmentEvent } from '@/types/twin'
import { useTwinStore } from '@/store/twinStore'
import { obs } from '@/core/observability'

// ─── Equipment health score ───────────────────────────────────
export function computeEquipmentHealth(eq: Equipment): {
  score:  number    // 0-100
  status: 'ok' | 'degraded' | 'critical' | 'offline'
  issues: string[]
} {
  const issues: string[] = []
  let score = 100

  if (eq.status === 'offline')     { issues.push('Equipamento offline'); score -= 60 }
  if (eq.status === 'critical')    { issues.push('Status crítico'); score -= 50 }
  if (eq.status === 'maintenance') { issues.push('Em manutenção'); score -= 30 }

  if (eq.uptime_pct < 70) { issues.push(`Uptime baixo: ${eq.uptime_pct}%`); score -= 20 }

  // Check overdue maintenance
  if (eq.next_maintenance) {
    const overdue = Date.now() - new Date(eq.next_maintenance).getTime()
    if (overdue > 0) {
      const days = Math.round(overdue / 86_400_000)
      issues.push(`Manutenção atrasada ${days} dia(s)`)
      score -= Math.min(30, days * 3)
    }
  }

  score = Math.max(0, score)
  const status: 'ok' | 'degraded' | 'critical' | 'offline' =
    score >= 80 ? 'ok' : score >= 50 ? 'degraded' : score > 0 ? 'critical' : 'offline'

  return { score, status, issues }
}

// ─── Update position ─────────────────────────────────────────
export async function updateEquipmentPosition(
  equipmentId: string,
  x:           number,
  y:           number,
  zoneId?:     string,
): Promise<void> {
  const { error } = await supabase
    .from('equipment_devices')
    .update({
      x,
      y,
      current_zone_id:   zoneId,
      last_position_at:  new Date().toISOString(),
      updated_at:        new Date().toISOString(),
    })
    .eq('id', equipmentId)

  if (error) { obs.warn('equipment_tracker', error.message); return }

  // updateEquipmentPosition não existe no twinStore (a chamada lançava
  // TypeError em runtime — mascarado por @ts-nocheck). Atualiza via
  // updateSingleEquipment, a primitiva real do store.
  const state = useTwinStore.getState()
  const current = state.twinState?.equipment.find(e => e.id === equipmentId)
  if (current) {
    state.updateSingleEquipment({
      ...current,
      x, y,
      current_zone_id:  zoneId ?? current.current_zone_id,
      last_position_at: new Date().toISOString(),
    })
  }
}

// ─── Log equipment event ──────────────────────────────────────
export async function logEquipmentEvent(
  equipmentId: string,
  obraId:      string,
  tipo:        EquipmentEvent['tipo'],
  detail?:     string,
): Promise<void> {
  await supabase.from('equipment_events').insert({
    equipment_id: equipmentId,
    obra_id:      obraId,
    tipo,
    detail,
  })
}

// ─── Get equipment near zone ──────────────────────────────────
export function getEquipmentInZone(
  equipment: Equipment[],
  zoneId:    string,
): Equipment[] {
  return equipment.filter(e => e.current_zone_id === zoneId)
}

// ─── Detect critical equipment ───────────────────────────────
export function getCriticalEquipment(equipment: Equipment[]): Equipment[] {
  return equipment.filter(e => {
    const { status } = computeEquipmentHealth(e)
    return status === 'critical' || status === 'offline'
  })
}

// ─── Crane reach zone check ───────────────────────────────────
export function isCraneReachZone(
  equipment: Equipment[],
  x:         number,
  y:         number,
  radiusNorm = 0.15,  // 15% of normalized space
): boolean {
  const cranes = equipment.filter(e => e.tipo === 'crane' && e.x !== undefined && e.y !== undefined)
  return cranes.some(c => Math.hypot((c.x ?? 0) - x, (c.y ?? 0) - y) <= radiusNorm)
}

// ─── Usage analytics ─────────────────────────────────────────
export async function getEquipmentEvents(
  equipmentId: string,
  hours:       number = 24,
): Promise<EquipmentEvent[]> {
  const since = new Date(Date.now() - hours * 3_600_000).toISOString()
  const { data } = await supabase
    .from('equipment_events')
    .select('*')
    .eq('equipment_id', equipmentId)
    .gte('ts', since)
    .order('ts', { ascending: false })
    .limit(100)

  return (data ?? []) as EquipmentEvent[]
}
